<nb-card>
  <nb-card-header class="d-flex justify-content-between align-items-center">
    <div>
      <h3>Email Themes</h3>
      <small>Create and manage theme variables and styling</small>
    </div>
    <div>
      <button nbButton status="primary" size="small" (click)="onCreateTheme()">Create Theme</button>
    </div>
  </nb-card-header>

  <nb-card-body>
    <div class="filters-row d-flex align-items-center" style="gap: 8px; margin-bottom: 12px">
      <nb-form-field>
        <nb-icon nbPrefix icon="toggle-left-outline"></nb-icon>
        <nb-select placeholder="Active" [formControl]="activeFilterControl" size="small">
          <nb-option [value]="true">Active</nb-option>
          <nb-option [value]="false">Inactive</nb-option>
        </nb-select>
      </nb-form-field>
      <nb-form-field>
        <nb-icon nbPrefix icon="layers-outline"></nb-icon>
        <nb-select placeholder="Type" [formControl]="systemThemeControl" size="small">
          <nb-option value="">All</nb-option>
          <nb-option value="false">Custom</nb-option>
          <nb-option value="true">System</nb-option>
        </nb-select>
      </nb-form-field>
      <div class="ml-auto text-muted">{{ themes.length }} themes</div>
    </div>

    <div class="themes-grid" *ngIf="!loading">
      <div
        class="theme-card"
        *ngFor="let theme of themes"
        [class.default]="theme.is_default"
        [class.inactive]="!theme.is_active"
        (click)="onEditTheme(theme)"
        [style.borderColor]="getPrimaryColor(theme)"
      >
        <div *ngIf="theme.is_default" class="default-label" [style.background]="getPrimaryColor(theme)">Default</div>
        <!-- Theme Header -->
        <div class="theme-header">
          <div class="theme-icon">
            <svg viewBox="0 0 24 24" class="icon">
              <path
                d="M12,15.5A3.5,3.5 0 0,1 8.5,12A3.5,3.5 0 0,1 12,8.5A3.5,3.5 0 0,1 15.5,12A3.5,3.5 0 0,1 12,15.5M19.43,12.97C19.47,12.65 19.5,12.33 19.5,12C19.5,11.67 19.47,11.34 19.43,11L21.54,9.37C21.73,9.22 21.78,8.95 21.66,8.73L19.66,5.27C19.54,5.05 19.27,4.96 19.05,5.05L16.56,6.05C16.04,5.66 15.5,5.32 14.87,5.07L14.5,2.42C14.46,2.18 14.25,2 14,2H10C9.75,2 9.54,2.18 9.5,2.42L9.13,5.07C8.5,5.32 7.96,5.66 7.44,6.05L4.95,5.05C4.73,4.96 4.46,5.05 4.34,5.27L2.34,8.73C2.22,8.95 2.27,9.22 2.46,9.37L4.57,11C4.53,11.34 4.5,11.67 4.5,12C4.5,12.33 4.53,12.65 4.57,12.97L2.46,14.63C2.27,14.78 2.22,15.05 2.34,15.27L4.34,18.73C4.46,18.95 4.73,19.03 4.95,18.95L7.44,17.94C7.96,18.34 8.5,18.68 9.13,18.93L9.5,21.58C9.54,21.82 9.75,22 10,22H14C14.25,22 14.46,21.82 14.5,21.58L14.87,18.93C15.5,18.68 16.04,18.34 16.56,17.94L19.05,18.95C19.27,19.03 19.54,18.95 19.66,18.73L21.66,15.27C21.78,15.05 21.73,14.78 21.54,14.63L19.43,12.97Z"
              />
            </svg>
          </div>
          <div class="theme-meta">
            <h3 class="theme-name">{{ theme.name }}</h3>
            <p class="theme-description">{{ theme.description || 'No description provided' }}</p>
          </div>
          <div class="theme-badges">
            <span class="status-badge" [class.active]="theme.is_active" [class.inactive]="!theme.is_active">
              {{ theme.is_active ? 'Active' : 'Inactive' }}
            </span>
            <span class="default-badge" *ngIf="theme.is_default">Default</span>
            <span class="system-badge" *ngIf="theme.is_system_theme">System</span>
          </div>
        </div>

        <!-- Theme Preview -->
        <div class="theme-preview">
          <div class="color-palette" *ngIf="theme.global_css">
            <div class="color-item" *ngFor="let color of getThemeColors(theme)">
              <div class="color-swatch" [style.background-color]="color.value"></div>
              <span class="color-label">{{ color.name }}</span>
            </div>
          </div>
          <div class="preview-gradient" [style.background]="getThemePreview(theme)"></div>
          <!-- Sample content preview -->
          <div
            class="sample-preview"
            [ngStyle]="{
              background: getBackground(theme),
              color: getTextColor(theme),
              fontFamily: getFontFamily(theme)
            }"
          >
            <div class="sample-header" [style.background]="getPrimaryColor(theme)">
              <div class="sample-logo"></div>
              <div class="sample-title">Sample Header</div>
            </div>
            <div class="sample-body">
              <div class="sample-h1">Heading styled by theme</div>
              <div class="sample-p">This is a short paragraph showing what text looks like with this theme. Colors and fonts are applied.</div>
              <div class="sample-actions">
                <button class="sample-btn" [ngStyle]="{ background: getPrimaryColor(theme) }">Primary</button>
                <button class="sample-btn ghost" [ngStyle]="{ color: getPrimaryColor(theme), borderColor: getPrimaryColor(theme) }">Ghost</button>
              </div>
            </div>
          </div>
        </div>

        <!-- Theme Info -->
        <div class="theme-info">
          <div class="info-item">
            <span class="label">Slug:</span>
            <code class="value">{{ theme.slug }}</code>
          </div>
          <div class="info-item" *ngIf="theme.variables && theme.variables.length > 0">
            <span class="label">Variables:</span>
            <span class="value">{{ theme.variables.length }} variable(s)</span>
          </div>
          <div class="info-item">
            <span class="label">Created:</span>
            <span class="value">{{ theme.created_at | date : 'short' }}</span>
          </div>
        </div>

        <!-- Theme Actions -->
        <div class="theme-actions">
          <button
            class="action-btn toggle"
            (click)="$event.stopPropagation(); onSetDefault(theme)"
            [disabled]="theme.is_default"
            title="Set as Default Theme"
            [ngStyle]="{ background: theme.is_default ? '#9ca3af' : getPrimaryColor(theme), color: 'white', borderColor: getPrimaryColor(theme) }"
          >
            <svg viewBox="0 0 24 24" class="icon">
              <path d="M12,17.27L18.18,21L16.54,13.97L22,9.24L14.81,8.62L12,2L9.19,8.62L2,9.24L7.46,13.97L5.82,21L12,17.27Z" />
            </svg>
            {{ theme.is_default ? 'Default' : 'Set Default' }}
          </button>
          <button
            class="action-btn danger"
            (click)="$event.stopPropagation(); onDeleteTheme(theme)"
            [disabled]="theme.is_system_theme || theme.is_default"
            [title]="theme.is_system_theme ? 'Cannot delete system theme' : theme.is_default ? 'Cannot delete default theme' : 'Delete Theme'"
          >
            <svg viewBox="0 0 24 24" class="icon">
              <path d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z" />
            </svg>
            Delete
          </button>
        </div>
      </div>
    </div>

    <div class="loading-state" *ngIf="loading">
      <div class="spinner"></div>
      <p>Loading themes...</p>
    </div>

    <div class="empty-state" *ngIf="!loading && themes.length === 0">
      <div class="empty-icon">
        <svg viewBox="0 0 24 24">
          <path
            d="M12,15.5A3.5,3.5 0 0,1 8.5,12A3.5,3.5 0 0,1 12,8.5A3.5,3.5 0 0,1 15.5,12A3.5,3.5 0 0,1 12,15.5M19.43,12.97C19.47,12.65 19.5,12.33 19.5,12C19.5,11.67 19.47,11.34 19.43,11L21.54,9.37C21.73,9.22 21.78,8.95 21.66,8.73L19.66,5.27C19.54,5.05 19.27,4.96 19.05,5.05L16.56,6.05C16.04,5.66 15.5,5.32 14.87,5.07L14.5,2.42C14.46,2.18 14.25,2 14,2H10C9.75,2 9.54,2.18 9.5,2.42L9.13,5.07C8.5,5.32 7.96,5.66 7.44,6.05L4.95,5.05C4.73,4.96 4.46,5.05 4.34,5.27L2.34,8.73C2.22,8.95 2.27,9.22 2.46,9.37L4.57,11C4.53,11.34 4.5,11.67 4.5,12C4.5,12.33 4.53,12.65 4.57,12.97L2.46,14.63C2.27,14.78 2.22,15.05 2.34,15.27L4.34,18.73C4.46,18.95 4.73,19.03 4.95,18.95L7.44,17.94C7.96,18.34 8.5,18.68 9.13,18.93L9.5,21.58C9.54,21.82 9.75,22 10,22H14C14.25,22 14.46,21.82 14.5,21.58L14.87,18.93C15.5,18.68 16.04,18.34 16.56,17.94L19.05,18.95C19.27,19.03 19.54,18.95 19.66,18.73L21.66,15.27C21.78,15.05 21.73,14.78 21.54,14.63L19.43,12.97Z"
          />
        </svg>
      </div>
      <h3>No Themes Found</h3>
      <p>Create your first email theme to get started</p>
      <button class="create-btn" (click)="onCreateTheme()">
        <svg viewBox="0 0 24 24" class="icon">
          <path d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z" />
        </svg>
        Create First Theme
      </button>
    </div>
  </nb-card-body>
</nb-card>
